import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'bem-cn';

import { useGetCourseQuery, useGetChaptersQuery } from '../../store/services/course';
import { useNavigateBack } from '../../hooks';
import type { Chapter } from '../../store/services/course/types';
import { WebAppLayout } from '../../layouts/WebAppLayout';
import { HeaderElements } from '../../components/Header';
import { TagList } from '../../components/TagList';
import { RoutePaths } from '../../constants/appRoutes';
import { ChapterCard } from '../../components/ChapterCard';
import { Link, Stack } from '../../components/UI';
import { BackButton } from '../../components/BackButton';
import { formatCourseTime } from './CoursePage.helpers';
import { CoursePageProps } from './CoursePage.types';

import './CoursePage.scss';

const b = cn('course-page');

export const CoursePage: React.FC<CoursePageProps> = () => {
    const { id } = useParams<{ id: string }>();
    const courseId = parseInt(id || '0', 10);

    const handleBackButtonClick = useNavigateBack(RoutePaths.Study);

    const headerElements: HeaderElements = useMemo(
        () => ({
            desktop: {
                left: { type: 'logo' },
                right: { type: 'profile' },
            },
            mobile: {
                left: { type: 'backButton', onClick: handleBackButtonClick },
                right: { type: 'profile' },
            },
        }),
        [handleBackButtonClick],
    );

    // Функция для склонения слова "глава"
    const getChapterWord = (count: number): string => {
        if (count === 1) {
            return 'глава';
        }
        if (count < 5) {
            return 'главы';
        }

        return 'глав';
    };

    const {
        data: course,
        isLoading: isCourseLoading,
        error: courseError,
    } = useGetCourseQuery({ courseId }, { skip: !courseId || isNaN(courseId) });

    const {
        data: chaptersData,
        isLoading: isChaptersLoading,
        error: chaptersError,
    } = useGetChaptersQuery({ courseId }, { skip: !courseId || isNaN(courseId) });

    if (!courseId || isNaN(courseId)) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('error')}>
                        <h1>Ошибка</h1>
                        <p>Некорректный ID курса</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    if (isCourseLoading || isChaptersLoading) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('loading')}>
                        <h1>Загрузка...</h1>
                        <p>Получаем информацию о курсе</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    if (courseError || chaptersError) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('error')}>
                        <h1>Ошибка</h1>
                        <p>Не удалось загрузить информацию о курсе</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    if (!course) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('error')}>
                        <h1>Курс не найден</h1>
                        <p>Курс с ID {courseId} не существует</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    const chapters = chaptersData?.chapters || [];

    const courseTags = [
        'Курс',
        `${course.chapters_count} ${getChapterWord(course.chapters_count || 0)}`,
        formatCourseTime(course.course_time),
    ].filter(tag => tag && tag.trim()); // Убираем пустые теги

    return (
        <WebAppLayout headerElements={headerElements} hideNavigation>
            <Stack className={b()}>
                <BackButton className={b('back-button')} />
                <div className={b('header')}>
                    <TagList tags={courseTags} maxVisible={3} />
                    <div className={b('content')}>
                        <h1 className={b('title')}>{course.name}</h1>
                        <p className={b('description')}>{course.description}</p>
                    </div>
                    <Link
                        to={`/course/${courseId}/chapter/1`}
                        className={b('start-button', { accent: true, desktop: true })}
                    >
                        Начать курс
                    </Link>
                </div>

                {chapters.length > 0 && (
                    <div className={b('chapters')}>
                        <h2 className={b('chapters-title')}>Программа курса</h2>
                        <div className={b('chapters-list')}>
                            {chapters.map((chapter: Chapter) => (
                                <Link key={chapter.id} to={`/course/${courseId}/chapter/${chapter.order_number}`}>
                                    <ChapterCard chapter={chapter} className={b('chapter-item')} />
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </Stack>
        </WebAppLayout>
    );
};
